/**
 * career-search — the flagship search surface (route `/search`).
 *
 * Shows the EFFECTIVE search config (returned by `/api/search/start`), a start
 * control, the LIVE push rail (the SearchRun store folded from the push plane,
 * `role="log" aria-live="polite"`, counts-with-meaning), and a real Stop control.
 * Opportunity cards populate as the detached run writes them: while a run is live
 * the surface polls `/api/opportunities` and reconciles them into the opportunity
 * store (each card upgrades verify → brief → match in place). Bookmark / dismiss /
 * delete are OPTIMISTIC store commands carrying the ABSOLUTE end-state; the next
 * reconcile folds server truth. All chrome flows through `t()`; card CONTENT
 * (authored brief, posting text, match quotes) is domain data, rendered as authored.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type PushChannel,
  type ReadonlySignal,
  type Router,
  type Signal,
  type Urn,
  type ViewModel,
} from "@platform/web-kit"
import { classMap, html, nothing, repeat, type TemplateResult } from "@platform/web-kit/render"
import {
  I18nToken,
  OpportunityStoreToken,
  PushToken,
  RouterToken,
  SearchRunStoreToken,
  SessionToken,
  apiGet,
  apiPost,
  isPaymentRequired,
} from "../services"
import type { CareerI18n, CareerKey } from "../../i18n"
import {
  careerStream,
  type OpportunityCardView,
  type OpportunityStore,
  type SearchRunStage,
  type SearchRunStatus,
  type SearchRunStore,
  type SearchRunView,
  type ShellSession,
} from "../domain"
import { opportunityShortId, renderOpportunityCard } from "../lib/opportunity-card"
import { skeletonListCards } from "../lib/skeleton"

/** The effective search config the run uses (client projection of SearchConfig). */
interface SearchConfig {
  roles: string[]
  queries: string[]
  seniority: string[]
  locations: string[]
  excludeKeywords: string[]
  summary: string
}
interface StartResponse {
  searchId: string
  streamId: string
  config: SearchConfig
  targetingReady: boolean
}
interface OpportunityWithMatch {
  opportunity: Record<string, unknown>
  match: Record<string, unknown> | null
  /** The enriched employer digest for this result's org (E3b), when the read model carries it. */
  org?: Record<string, unknown>
}

type SearchConfigListKey = "roles" | "queries" | "seniority" | "locations" | "excludeKeywords"
const CONFIG_FIELDS: { key: SearchConfigListKey; label: CareerKey }[] = [
  { key: "roles", label: "workshop.targeting.roles" },
  { key: "queries", label: "workshop.targeting.queries" },
  { key: "seniority", label: "workshop.targeting.seniority" },
  { key: "locations", label: "workshop.targeting.locations" },
  { key: "excludeKeywords", label: "workshop.targeting.exclusions" },
]

// The live-rail vocabulary is the generic search-run wording (shared with heartbeat).
const STAGE_KEY: Record<SearchRunStage, CareerKey> = {
  discovering: "heartbeat.stage.discovering",
  verifying: "heartbeat.stage.verifying",
  matching: "heartbeat.stage.matching",
  enriching: "heartbeat.stage.enriching",
  done: "heartbeat.stage.done",
}
const STATUS_KEY: Record<SearchRunStatus, CareerKey> = {
  running: "heartbeat.status.running",
  completed: "heartbeat.status.completed",
  stopped: "heartbeat.status.stopped",
  failed: "heartbeat.status.failed",
}

const POLL_MS = 2500

export class SearchVm implements ViewModel {
  readonly runs: ReadonlySignal<readonly SearchRunView[]>
  readonly opportunities: ReadonlySignal<readonly OpportunityCardView[]>
  readonly bookmarked: ReadonlySignal<readonly OpportunityCardView[]>
  readonly config: Signal<SearchConfig | null> = signal<SearchConfig | null>(null)
  readonly targetingReady: Signal<boolean> = signal(true)
  readonly activeSearchId: Signal<string | null> = signal<string | null>(null)
  readonly starting: Signal<boolean> = signal(false)
  readonly stopping: Signal<boolean> = signal(false)
  readonly error: Signal<string | null> = signal<string | null>(null)
  readonly loaded: Signal<boolean> = signal(false)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]
  /** SearchRun streams opened this session (subscribe REPLACES the set). */
  private readonly streams = new Set<string>()
  /** Monotonic version stamp for fetch-reconciled synthetic envelopes (idempotency law). */
  private seq = 1
  private timer: ReturnType<typeof setInterval> | null = null

  constructor(
    private readonly searchStore: SearchRunStore,
    private readonly store: OpportunityStore,
    private readonly push: PushChannel,
    private readonly router: Router,
    private readonly session: ShellSession,
    i18n: CareerI18n,
  ) {
    this.t = i18n.t
    this.runs = searchStore.list("aktiv")
    this.opportunities = store.list("alle")
    this.bookmarked = store.list("gemerkt")
    this.renderDeps = [
      this.runs,
      this.opportunities,
      this.bookmarked,
      this.config,
      this.targetingReady,
      this.activeSearchId,
      this.starting,
      this.stopping,
      this.error,
      this.loaded,
      store.pending,
    ]
  }

  private get userId(): string | null {
    return this.session.user?.sub ?? null
  }

  attach(): () => void {
    void this.hydrate()
    return () => this.stopPolling()
  }

  /** The run currently being driven (the started search), if any. */
  currentRun(): SearchRunView | null {
    const id = this.activeSearchId.value
    return id ? (this.searchStore.entity(id as Urn).value ?? null) : (this.runs.value[0] ?? null)
  }

  isRunning(): boolean {
    return this.currentRun()?.status === "running"
  }

  /**
   * Set the surface error from a caught exception — EXCEPT the credits-block (402 insufficient /
   * entitlement 403): that's not a surface failure, it's a product state the shared shell notice
   * already owns (link to /credits). Swallow it here so the surface stays calm (its empty state)
   * instead of shouting a raw „…failed: HTTP 403". Real failures still surface normally.
   */
  private setError(e: unknown): void {
    if (isPaymentRequired(e)) {
      this.error.set(null)
      return
    }
    this.error.set(e instanceof Error ? e.message : String(e))
  }

  async start(): Promise<void> {
    this.starting.set(true)
    this.error.set(null)
    try {
      const res = await apiPost<StartResponse>("/api/search/start")
      this.config.set(res.config)
      this.targetingReady.set(res.targetingReady)
      this.activeSearchId.set(res.searchId)
      this.streams.add(res.streamId)
      // Live rail: the SearchRun aggregate carries liveness over the push plane.
      this.push.subscribe([...this.streams])
      await this.hydrate() // reconcile any results already written, then poll for more
      this.startPolling()
    } catch (e) {
      this.setError(e)
    } finally {
      this.starting.set(false)
    }
  }

  async stop(): Promise<void> {
    const id = this.activeSearchId.value
    if (!id) return
    this.stopping.set(true)
    this.error.set(null)
    try {
      await apiPost("/api/search/stop", { searchId: id })
    } catch (e) {
      this.setError(e)
    } finally {
      this.stopping.set(false)
      await this.hydrate() // final reconcile
      this.stopPolling()
    }
  }

  bookmark(opportunityId: string): void {
    void this.store.command({ type: "bookmark", payload: { opportunityId } })
  }
  dismiss(opportunityId: string): void {
    void this.store.command({ type: "dismiss", payload: { opportunityId } })
  }
  remove(opportunityId: string): void {
    void this.store.command({ type: "delete", payload: { opportunityId } })
  }

  go(url: string): void {
    void this.router.navigate(url)
  }

  private startPolling(): void {
    this.stopPolling()
    this.timer = setInterval(() => {
      void this.hydrate().then(() => {
        if (!this.isRunning()) this.stopPolling()
      })
    }, POLL_MS)
  }
  private stopPolling(): void {
    if (this.timer !== null) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  /** Reconcile the owner's opportunities into the store (each at a fresh version). */
  private async hydrate(): Promise<void> {
    const userId = this.userId
    try {
      const items = await apiGet<OpportunityWithMatch[]>("/api/opportunities")
      if (userId) {
        for (const { opportunity, match, org } of items) {
          const id = opportunity["id"]
          if (typeof id !== "string") continue
          const streamId = careerStream(userId, "Opportunity", id)
          this.store.apply({ streamId, version: this.seq++, type: "opportunity.Discovered", payload: { owner: userId, opportunity, ...(org ? { org } : {}) } })
          if (match) {
            this.store.apply({ streamId, version: this.seq++, type: "opportunity.Matched", payload: { owner: userId, opportunityId: id, match } })
          }
        }
      }
    } catch (e) {
      this.setError(e)
    } finally {
      this.loaded.set(true)
    }
  }
}

export class CareerSearch extends KitElement<SearchVm> {
  protected createViewModel(ctx: KitContext): SearchVm {
    return new SearchVm(
      ctx.inject(SearchRunStoreToken),
      ctx.inject(OpportunityStoreToken),
      ctx.inject(PushToken),
      ctx.inject(RouterToken),
      ctx.inject(SessionToken),
      ctx.inject(I18nToken),
    )
  }

  private configPanel(vm: SearchVm): TemplateResult {
    const cfg = vm.config.value
    if (!cfg) return nothing as unknown as TemplateResult
    return html`
      <section class="search-config surface-panel" aria-labelledby="search-config-title">
        <h2 id="search-config-title" class="surface-section-title" lang="de">${vm.t("search.config.title")}</h2>
        ${cfg.summary ? html`<p class="search-config-summary" lang="de">${cfg.summary}</p>` : nothing}
        <dl class="search-config-list">
          ${CONFIG_FIELDS.map(
            (f) => html`<dt lang="de">${vm.t(f.label)}</dt>
              <dd lang="de">${cfg[f.key].length > 0 ? cfg[f.key].join(", ") : vm.t("search.config.empty")}</dd>`,
          )}
        </dl>
      </section>
    `
  }

  private railRun(vm: SearchVm, run: SearchRunView): TemplateResult {
    return html`
      <li class=${classMap({ "search-run": true, [`is-${run.status}`]: true })} data-run-id=${run.urn}>
        <span class="run-stage chip" lang="de">${vm.t(STAGE_KEY[run.stage])}</span>
        <span class="run-status" lang="de">${vm.t(STATUS_KEY[run.status])}</span>
        <span class="run-count chip" lang="de">${vm.t("heartbeat.count.discovered", { n: run.discovered })}</span>
        <span class="run-count chip" lang="de">${vm.t("heartbeat.count.verified", { n: run.verified })}</span>
        <span class="run-count chip" lang="de">${vm.t("heartbeat.count.matched", { n: run.matched })}</span>
      </li>
    `
  }

  protected render(vm: SearchVm): TemplateResult {
    const runs = vm.runs.value
    const opps = vm.opportunities.value
    const saved = vm.bookmarked.value
    const running = vm.isRunning()
    const err = vm.error.value
    return html`
      <section class="surface search" aria-labelledby="search-title">
        <h1 id="search-title" class="surface-title" lang="de">${vm.t("surface.suche.title")}</h1>
        <p class="surface-lead" lang="de">${vm.t("search.lead")}</p>
        ${err ? html`<p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: err })}</p>` : nothing}

        <div class="search-controls" role="group" aria-label=${vm.t("surface.suche.title")}>
          ${running
            ? html`<button
                data-act="stop-search"
                class=${classMap({ "search-stop": true, "is-pending": vm.stopping.value })}
                ?disabled=${vm.stopping.value}
                lang="de"
              >
                ${vm.stopping.value ? vm.t("search.stopping") : vm.t("search.stop")}
              </button>`
            : html`<button
                data-act="start-search"
                class=${classMap({ "search-start": true, "is-pending": vm.starting.value, "is-x-prominent": true })}
                ?disabled=${vm.starting.value}
                lang="de"
              >
                ${vm.starting.value ? vm.t("search.starting") : vm.t("search.start")}
              </button>`}
        </div>

        ${!vm.targetingReady.value
          ? html`<p class="search-targeting-hint surface-gated" lang="de">
              ${vm.t("search.targetingHint")}
              <a href="/workshops/search-profile" data-act="nav" data-url="/workshops/search-profile" lang="de">${vm.t("search.targetingHint.action")}</a>
            </p>`
          : nothing}

        ${this.configPanel(vm)}

        <section class="search-rail-section" aria-labelledby="search-rail-title">
          <h2 id="search-rail-title" class="surface-section-title" lang="de">${vm.t("search.rail.title")}</h2>
          <ol class="search-rail" role="log" aria-live="polite" aria-label=${vm.t("search.rail.title")}>
            ${runs.length === 0
              ? html`<li class="search-rail-empty" lang="de">${vm.t("search.rail.empty")}</li>`
              : repeat(runs, (r) => r.urn, (r) => this.railRun(vm, r))}
          </ol>
        </section>

        <section class="search-results" aria-labelledby="search-results-title" aria-live="polite">
          <h2 id="search-results-title" class="surface-section-title" lang="de">${vm.t("search.results.title")}</h2>
          ${opps.length === 0
            ? !vm.loaded.value && !running
              ? html`<ul class="opportunity-list" role="list" aria-busy="true">${skeletonListCards(3)}</ul>`
              : html`<p class="surface-empty" lang="de">${running ? vm.t("search.results.running") : vm.t("search.results.empty")}</p>`
            : html`<ul class="opportunity-list" role="list">
                ${repeat(opps, (o) => o.urn, (o) => renderOpportunityCard(o, vm.t))}
              </ul>`}
        </section>

        ${saved.length > 0
          ? html`<section class="search-saved" aria-labelledby="search-saved-title">
              <h2 id="search-saved-title" class="surface-section-title" lang="de">${vm.t("search.bookmarked.title")}</h2>
              <ul class="opportunity-list is-x-saved" role="list">
                ${repeat(saved, (o) => o.urn, (o) => renderOpportunityCard(o, vm.t))}
              </ul>
            </section>`
          : nothing}
      </section>
    `
  }

  protected override actions(vm: SearchVm): ActionMap {
    return {
      "start-search": () => void vm.start(),
      "stop-search": () => void vm.stop(),
      bookmark: ({ opportunity }) => {
        if (opportunity) vm.bookmark(opportunity)
      },
      dismiss: ({ opportunity }) => {
        if (opportunity) vm.dismiss(opportunity)
      },
      delete: ({ opportunity }) => {
        if (opportunity) vm.remove(opportunity)
      },
      "open-opportunity": ({ opportunity }, event) => {
        event.preventDefault()
        if (opportunity) vm.go(`/search/opportunities/${opportunityShortId(opportunity)}`)
      },
      nav: ({ url }, event) => {
        event.preventDefault()
        if (url) vm.go(url)
      },
    }
  }
}
